/**
 * Permission guard helpers for TanStack Router `beforeLoad` in the admin app.
 *
 * Mirror of the client app's `permission-guard.ts` adapted for the admin
 * permission domain (`SystemAdmin.*`). These read from the cached
 * `admin-user-context` TanStack Query and throw a redirect to
 * `/access-denied` if the caller lacks the required permission.
 *
 * Security contract:
 *   The backend is authoritative — every admin handler re-checks via
 *   `EnsureAdminPermission(...)`. These guards are pure UX: keep users from
 *   landing on pages the backend would 403, so the experience degrades to
 *   a friendly "access denied" page rather than a half-loaded view with an
 *   error toast.
 *
 * Usage:
 *
 *     import { Permissions } from "@/constants/permissions";
 *     import { requirePermission } from "@/lib/auth/permission-guard";
 *
 *     export const Route = createFileRoute("/agencies/")({
 *       beforeLoad: (args) =>
 *         requirePermission(Permissions.Agencies.ViewAll, args),
 *       component: AgenciesListPage,
 *     });
 *
 * Notes:
 *   - Unauthenticated requests cause the context fetch to fail; we swallow
 *     that and let the root route's MSAL `<UnauthenticatedTemplate>` take
 *     over (login screen) rather than bouncing to `/access-denied`.
 *   - The redirect preserves the original `href` as `from` so the
 *     access-denied page can offer a back-link.
 */

import type { Permission } from "@/constants/permissions";
import { adminUserContextService } from "@/services/api/admin-user-context-service";
import { useAdminUserContextStore } from "@/stores/admin-user-context-store";
import type { AdminUserContext } from "@/types/auth";
import type { QueryClient } from "@tanstack/react-query";
import { redirect } from "@tanstack/react-router";

type RouterContext = { queryClient: QueryClient };

export type GuardArgs = {
	context: RouterContext;
	location: { href: string; pathname: string };
};

/**
 * Fetch (or read from cache) the admin user context. Mirrors the query key
 * + staleTime used by `useAdminUserContext` so the two share a cache entry.
 *
 * Returns null when the fetch fails — almost always an auth error.
 * Caller treats null as "let the root auth flow handle it" rather than
 * "redirect to access-denied" (the user isn't denied, they're not signed in).
 */
async function ensureContext(
	queryClient: QueryClient,
): Promise<AdminUserContext | null> {
	try {
		const data = await queryClient.ensureQueryData({
			queryKey: ["admin-user-context"],
			queryFn: () => adminUserContextService.getCurrent(),
			staleTime: 5 * 60 * 1000,
		});

		// Mirror into the sync store so non-React consumers (sidebar
		// filtering, etc.) read the same source of truth.
		if (data) {
			useAdminUserContextStore.getState().setContext(data);
		}

		return data;
	} catch (err) {
		console.warn(
			"[permission-guard] admin-user-context fetch failed; skipping guard",
			err,
		);
		return null;
	}
}

function denyAccess(required: string, from: string): never {
	throw redirect({
		to: "/access-denied",
		search: { required, from },
	});
}

/**
 * Require the caller to hold the given permission. Redirects to
 * `/access-denied` if absent.
 */
export async function requirePermission(
	permission: Permission,
	args: GuardArgs,
): Promise<void> {
	const ctx = await ensureContext(args.context.queryClient);
	if (!ctx) return; // auth not ready — let MSAL flow take over
	if (!ctx.permissions.includes(permission)) {
		denyAccess(permission, args.location.href);
	}
}

/**
 * Require the caller to hold AT LEAST ONE of the given permissions. Used
 * when multiple distinct permissions can grant access to the same surface.
 */
export async function requireAnyPermission(
	permissions: readonly Permission[],
	args: GuardArgs,
): Promise<void> {
	const ctx = await ensureContext(args.context.queryClient);
	if (!ctx) return;
	if (!permissions.some((p) => ctx.permissions.includes(p))) {
		denyAccess(permissions.join(" OR "), args.location.href);
	}
}

/**
 * Require the caller to hold EVERY one of the given permissions.
 */
export async function requireAllPermissions(
	permissions: readonly Permission[],
	args: GuardArgs,
): Promise<void> {
	const ctx = await ensureContext(args.context.queryClient);
	if (!ctx) return;
	const missing = permissions.find((p) => !ctx.permissions.includes(p));
	if (missing) {
		denyAccess(missing, args.location.href);
	}
}
